import { computed, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardAuthRequest,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
  RedirectRequest,
} from '@azure/msal-browser';
import {
  catchError,
  filter,
  lastValueFrom,
  map,
  of,
  shareReplay,
  startWith,
  Subject,
  takeUntil,
} from 'rxjs';
import { environment } from '../../../../environments/environment';
import { User } from '../../models/user';
import { AuthCookieService } from '../auth-cookie/auth-cookie.service';
import { UserService } from '../user/user.service';

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
  private msalService = inject(MsalService);
  private userService = inject(UserService);
  private authCookieService = inject(AuthCookieService);
  private msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
  private msalBroadcastService = inject(MsalBroadcastService);

  private loginRequest: MsalGuardAuthRequest = this.msalGuardConfig
    .authRequest as MsalGuardAuthRequest;
  private destroy$ = new Subject<void>();

  /** Set when we have definitively checked auth (true after redirect/silent/no-cookie paths resolve) */
  readonly authCheckedSig = signal<boolean>(false);

  /** Current user (null when signed out or not yet loaded) */
  readonly userSig = signal<User | null>(null);

  readonly tokenExpirationSig = signal<Date | null>(null);

  /** Derived: authed iff we have a user and they’re active */
  readonly isAuthenticatedSig = computed(() => {
    const u = this.userSig();
    return this.userSig() !== null;
  });

  readonly isAuthorizedSig = computed(() => this.userSig()?.isActive);

  /** MSAL interaction busy (redirect/acquire token, etc.) */
  private readonly msalBusy$ = this.msalBroadcastService.inProgress$.pipe(
    map((s) => s !== InteractionStatus.None),
    startWith(false), // 🚫 don’t start “true” or you’ll get stuck on spinner
    shareReplay(1),
  );
  /** Busy as a signal (for templates) */
  readonly isMsalBusySig = toSignal(this.msalBusy$, { initialValue: false });

  /** Unified busy for UI (MSAL busy OR we haven't finished the first auth check) */
  readonly isBusySig = computed(() => this.isMsalBusySig() || !this.authCheckedSig());

  // ---- API ----

  get signedInUser() {
    return this.userSig();
  }

  readonly isAdminSig = computed(() => this.userSig()?.role === 'Admin');

  /** Lead or Admin — can manage the team's plan assignments (manager view). */
  readonly isManagerSig = computed(() => {
    const role = this.userSig()?.role;
    return role === 'Admin' || role === 'Lead';
  });

  /** sessionStorage key holding an invite token captured from the /invite link. */
  private static readonly INVITE_TOKEN_KEY = 'pendingInviteToken';

  initialize() {
    // Capture the invite token from the /invite?token=… link BEFORE MSAL redirects
    // away; it survives the round-trip in sessionStorage and drives redemption on
    // return. Must run before login() below.
    this.captureInviteToken();

    this.msalService.instance.initialize().then(() => {
      this.msalService.instance
        .handleRedirectPromise()
        .then((result) => {
          if (result) {
            this.onAuthenticationSuccess(result, false);
          } else if (this.activeAccount) {
            this.acquireTokenSilent();
          } else {
            this.login();
            this.authCheckedSig.set(true);
          }
        })
        .catch((err) => {
          // Nothing downstream would set authCheckedSig, so a rejection here would
          // strand the app on its loading state.
          console.error('MSAL handleRedirectPromise failed:', err);
          this.authCheckedSig.set(true);
        })
        .finally(() => {
          // MsalService.handleRedirectObservable() ends its chain with this; we
          // hand-roll initialize()/handleRedirectPromise() so we must do it here.
          // inProgress$ starts at InteractionStatus.Startup and only leaves it on a
          // status-changing event. handleRedirectPromise returns early (emitting
          // nothing) when no interaction matches this clientId, and a loginRedirect
          // that throws at setInteractionInProgress never emits LOGIN_START either --
          // so without this reset inProgress$ stays Startup, isMsalBusySig() stays
          // true, and the app sits on a spinner forever.
          this.msalBroadcastService.resetInProgressEvent();
        });

      this.msalService.instance.enableAccountStorageEvents();
    });

    // Record auth failures here; do NOT start another redirect from this handler.
    // loginRedirect throws `interaction_in_progress` when one is already pending, and
    // MSAL re-emits that throw as LOGIN_FAILURE/ACQUIRE_TOKEN_FAILURE, which feeds
    // straight back into this subscription and recurses until the call stack blows.
    // Starting a redirect belongs to initialize() and the acquireTokenSilent() catch
    // below -- the only two places that know an interaction isn't already underway.
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((event: EventMessage) =>
          ([EventType.LOGIN_FAILURE, EventType.ACQUIRE_TOKEN_FAILURE] as string[]).includes(
            event.eventType,
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((event: EventMessage) => {
        console.error(`MSAL ${event.eventType}:`, event.error);
        // Release the UI from its loading state: a failure arriving outside the two
        // redirect paths would otherwise leave isBusySig() stuck true forever.
        this.authCheckedSig.set(true);
      });
  }

  login(): void {
    // will login without redirect if KMSI active; redirect otherwise
    const req = this.loginRequest ? { ...(this.loginRequest as RedirectRequest) } : undefined;
    this.msalService.loginRedirect(req);
  }

  logout(): void {
    this.authCookieService.deleteCookie();
    this.msalService.logoutRedirect({ account: this.activeAccount });
  }

  clearCache(): void {
    this.msalService.instance.clearCache();
    window.location.href = '/'; //refresh to force login redirect
  }

  refreshToken(): void {
    this.acquireTokenSilent();
  }

  private _verifyOk = false;
  private _verifyAt = 0;
  private _verifyInFlight: Promise<boolean> | null = null;
  private VERIFY_TTL_MS = 60_000; // 1 min

  verifyTokenIfNeeded<T>(fn: () => import('rxjs').Observable<T>): Promise<boolean> {
    const now = Date.now();

    // Fresh success → allow
    if (this._verifyOk && now - this._verifyAt < this.VERIFY_TTL_MS) {
      return Promise.resolve(true);
    }

    // Reuse in-flight request
    if (this._verifyInFlight) return this._verifyInFlight;

    this._verifyInFlight = lastValueFrom(
      fn().pipe(
        map(() => true),
        catchError(() => of(false)),
      ),
    ).then((ok) => {
      this._verifyOk = ok;
      this._verifyAt = Date.now();
      this._verifyInFlight = null;
      return ok;
    });

    return this._verifyInFlight;
  }

  // ---- internals ----

  private acquireTokenSilent(): void {
    this.msalService.instance
      .acquireTokenSilent({
        scopes: environment.loginApiConfig.scopes,
        account: this.activeAccount,
        prompt: 'none',
        forceRefresh: false,
      })
      .then((result) => this.onAuthenticationSuccess(result, true))
      .catch(() => {
        this.login(); // on failure, try login (will redirect if KMSI not active)
        this.authCheckedSig.set(true);
      });
  }

  private onAuthenticationSuccess(
    authResult: AuthenticationResult | null,
    isSilent?: boolean,
  ): void {
    if (authResult) {
      this.msalService.instance.setActiveAccount(authResult.account);
      this.buildCookie(authResult.accessToken, new Date(authResult.expiresOn!));
      this.authorizeUser(this.getUserByClaims(authResult.idTokenClaims), isSilent || false);
    } else {
      this.authCheckedSig.set(true);
    }
  }

  private buildCookie(accessToken: string, expiresOn: Date) {
    this.tokenExpirationSig.set(expiresOn);
    this.authCookieService.accessToken = accessToken;
  }

  private authorizeUser(user: User, isSilentLogin: boolean): void {
    // A pending invite token (captured from the /invite link) takes priority: redeem
    // it deterministically, binding this Entra identity to the invited record. Falls
    // back to the normal lookup/provision paths when there's no token.
    const inviteToken = this.pendingInviteToken();
    const api$ = inviteToken
      ? this.userService.redeemInvite(inviteToken)
      : isSilentLogin
        ? this.userService.getUserByAzureId(user.azureId).pipe(
            // A returning identity resolves via the silent path; getUserByAzureId is
            // read-only and errors before the identity exists, so fall back to
            // get-or-insert (which provisions an inactive user for admin approval).
            catchError(() => this.userService.getOrInsertUser(user)),
          )
        : this.userService.getOrInsertUser(user);

    api$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (u) => {
        // redemption is single-use; clear the token once consumed
        if (inviteToken) this.clearPendingInviteToken();
        // update all auth signals
        this.userSig.set(u);
        if (!u.isActive) this.authCookieService.deleteCookie();
        this.authCheckedSig.set(true);
      },
      error: () => {
        // clear a bad/expired token so we don't loop on it every refresh
        if (inviteToken) this.clearPendingInviteToken();
        this.userSig.set(null);
        this.authCookieService.deleteCookie();
        this.authCheckedSig.set(true);
      },
    });
  }

  /** Stash an invite token from the current /invite?token=… URL, if present. */
  private captureInviteToken(): void {
    try {
      const { pathname, search } = window.location;
      if (!pathname.replace(/\/+$/, '').endsWith('/invite')) return;
      const token = new URLSearchParams(search).get('token');
      if (token) sessionStorage.setItem(AuthService.INVITE_TOKEN_KEY, token);
    } catch {
      // sessionStorage/URL unavailable — nothing to capture
    }
  }

  private pendingInviteToken(): string | null {
    try {
      return sessionStorage.getItem(AuthService.INVITE_TOKEN_KEY);
    } catch {
      return null;
    }
  }

  private clearPendingInviteToken(): void {
    try {
      sessionStorage.removeItem(AuthService.INVITE_TOKEN_KEY);
    } catch {
      // ignore
    }
  }

  private get activeAccount() {
    return (
      this.msalService.instance.getActiveAccount() ?? this.msalService.instance.getAllAccounts()[0]
    );
  }

  private getUserByClaims(claims: any): User {
    return {
      azureId: claims.oid,
      firstName: claims['given_name'],
      lastName: claims['family_name'],
      email: claims['email'] ?? claims['preferred_username'] ?? claims['upn'],
    } as User;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
